import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { GraduationCap, AlertCircle, Info, User, Printer } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AnimatedAnnouncementFeed } from "@/components/announcements/AnimatedAnnouncementFeed";
import { ResultAccessGate } from "./ResultAccessGate";
import { escapeHtml } from "@/lib/escapeHtml";
import { useMemo, useCallback } from "react";
import { useImpersonation } from "@/contexts/ImpersonationContext";

function remarkColor(remark: string | null) {
  if (!remark) return "secondary";
  const r = remark.toUpperCase();
  if (r === "PASS") return "default" as const;
  if (r === "SUPP" || r === "SUPPLEMENTARY") return "outline" as const;
  if (r === "FAIL") return "destructive" as const;
  return "secondary" as const;
}

function remarkClass(remark: string | null) {
  if (!remark) return "";
  const r = remark.toUpperCase();
  if (r === "PASS") return "bg-green-100 text-green-800 border-green-200";
  if (r === "SUPP" || r === "SUPPLEMENTARY") return "bg-orange-100 text-orange-800 border-orange-200";
  if (r === "FAIL") return "bg-red-100 text-red-800 border-red-200";
  return "";
}

interface GroupedSession {
  key: string;
  academicYear: string;
  semester: string;
  examSession: string;
  results: Array<{
    id: string;
    moduleCode: string;
    moduleName: string;
    grade: string | null;
    marks: number | null;
    remark: string | null;
    gpa: string | null;
    overall_remark: string | null;
  }>;
}

export function StudentExaminationView() {
  const { user } = useAuth();
  const { mode, simulatedStudent } = useImpersonation();
  const isSimulating = mode === "student_simulation" && simulatedStudent !== null;

  const { data: realStudent } = useQuery({
    queryKey: ["my-student-record", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("students")
        .select("id, first_name, last_name, student_number, class, program, nta_level, form_iv_index")
        .eq("user_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user && !isSimulating,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  // When simulating, fetch the student by ID or build from pre-reg data
  const { data: simStudent } = useQuery({
    queryKey: ["simulated-student-record", simulatedStudent?.studentId, simulatedStudent?.formIvIndex],
    queryFn: async () => {
      if (simulatedStudent?.studentId) {
        const { data } = await supabase
          .from("students")
          .select("id, first_name, last_name, student_number, class, program, nta_level, form_iv_index")
          .eq("id", simulatedStudent.studentId)
          .maybeSingle();
        return data;
      }
      // Pre-reg only — build a minimal object
      return {
        id: simulatedStudent!.preRegistrationId!,
        first_name: simulatedStudent!.fullName.split(" ")[0] || "Student",
        last_name: simulatedStudent!.fullName.split(" ").slice(1).join(" ") || "",
        student_number: "—",
        class: simulatedStudent!.studentClass,
        program: null,
        nta_level: null,
        form_iv_index: simulatedStudent!.formIvIndex,
      };
    },
    enabled: isSimulating,
  });

  const student = isSimulating ? simStudent : realStudent;

  const { data: announcements = [] } = useQuery({
    queryKey: ["exam-announcements"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("announcements")
        .select("id, title, content, priority, category, created_at, expires_at, file_url, target_classes")
        .eq("published", true)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []).filter((a) => {
        const isExamCategory = a.category === "examination" || a.category === "exam";
        const notExpired = !a.expires_at || new Date(a.expires_at) > new Date();
        const classMatch = !a.target_classes || a.target_classes.length === 0 || (student?.class && a.target_classes.includes(student.class));
        return isExamCategory && notExpired && classMatch;
      });
    },
    enabled: !!student,
  });

  const { data: results = [] } = useQuery({
    queryKey: ["my-exam-results", student?.id, student?.form_iv_index],
    queryFn: async () => {
      const { data: directResults, error: e1 } = await supabase
        .from("exam_results")
        .select("id, marks, grade, semester, academic_year, exam_type, status, course_id, remark, gpa, courses(course_code, course_name)")
        .eq("student_id", student!.id)
        .eq("status", "published")
        .order("academic_year", { ascending: false });
      if (e1) throw e1;

      let preRegResults: typeof directResults = [];
      if (student?.form_iv_index) {
        const { data: fivResults, error: e2 } = await supabase
          .from("exam_results")
          .select("id, marks, grade, semester, academic_year, exam_type, status, course_id, remark, gpa, courses(course_code, course_name)")
          .eq("form_iv_index", student.form_iv_index)
          .eq("status", "published")
          .is("student_id", null)
          .order("academic_year", { ascending: false });
        if (e2) throw e2;
        preRegResults = fivResults ?? [];
      }

      const allResults = [...(directResults ?? []), ...(preRegResults ?? [])];
      const seen = new Set<string>();
      return allResults.filter((r) => {
        if (seen.has(r.id)) return false;
        seen.add(r.id);
        return true;
      });
    },
    enabled: !!student?.id,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  // Fetch modules for name resolution
  const { data: modules = [] } = useQuery({
    queryKey: ["student-modules", student?.class],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("modules")
        .select("module_code, module_name")
        .eq("class", student!.class!);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!student?.class,
  });

  // Group results by session
  const grouped = useMemo<GroupedSession[]>(() => {
    const moduleMap = new Map(modules.map((m) => [m.module_code.trim(), m.module_name]));
    const groups = new Map<string, GroupedSession>();

    results.forEach((r: any) => {
      // Parse exam_type like "ES I - CMT 04101"
      const parts = (r.exam_type || "").split(" - ");
      const examSession = parts[0]?.trim() || r.exam_type || "—";
      const moduleCode = parts[1]?.trim() || "";

      const key = `${r.academic_year}__${r.semester}__${examSession}`;

      if (!groups.has(key)) {
        groups.set(key, {
          key,
          academicYear: r.academic_year,
          semester: r.semester,
          examSession,
          results: [],
        });
      }

      groups.get(key)!.results.push({
        id: r.id,
        moduleCode,
        moduleName: moduleMap.get(moduleCode) || r.courses?.course_name || "—",
        grade: r.grade,
        marks: r.marks,
        remark: r.remark,
        gpa: r.gpa,
        overall_remark: r.overall_remark || null,
      });
    });

    // Sort results within each group by module code
    groups.forEach((g) => g.results.sort((a, b) => a.moduleCode.localeCompare(b.moduleCode)));

    return Array.from(groups.values());
  }, [results, modules]);

  const handlePrint = useCallback(() => {
    if (!student || grouped.length === 0) return;

    const remarkBadge = (remark: string | null) => {
      if (!remark) return "—";
      const r = remark.toUpperCase();
      const color = r === "PASS" ? "#166534" : r === "SUPP" || r === "SUPPLEMENTARY" ? "#9a3412" : r === "FAIL" ? "#991b1b" : "#374151";
      const bg = r === "PASS" ? "#dcfce7" : r === "SUPP" || r === "SUPPLEMENTARY" ? "#ffedd5" : r === "FAIL" ? "#fee2e2" : "#f3f4f6";
      return `<span style="background:${bg};color:${color};padding:2px 8px;border-radius:4px;font-size:11px;font-weight:600">${escapeHtml(remark)}</span>`;
    };

    const sessionsHtml = grouped.map((group) => {
      const storedOverall = group.results.find((r) => r.overall_remark)?.overall_remark;
      let overallRemark = storedOverall || null;
      if (!overallRemark && group.results.length > 0) {
        const remarks = group.results.map(r => r.remark?.toUpperCase()).filter(Boolean);
        if (remarks.some(r => r === "DISCO" || r === "DISCONTINUED")) overallRemark = "DISCONTINUED";
        else if (remarks.some(r => r === "FAIL")) overallRemark = "FAIL";
        else if (remarks.some(r => r === "SUPP" || r === "SUPPLEMENT" || r === "SUPPLEMENTARY")) overallRemark = "SUPP";
        else overallRemark = "PASS";
      }
      const gpa = group.results.find((r) => r.gpa)?.gpa;
      const modulesToRetake = group.results.filter((r) => {
        const rm = r.remark?.toUpperCase();
        const g = r.grade?.toUpperCase();
        return rm === "SUPP" || rm === "SUPPLEMENTARY" || rm === "FAIL" || rm === "DISCONTINUED" || rm === "DISCO" || rm === "INCOMPLETE" || g === "F" || g === "ABS";
      });
      const getAction = (remark: string | null) => {
        if (!remark) return null;
        const r = remark.toUpperCase();
        if (r === "PASS") return "Proceed to Next Semester";
        if (r === "SUPP" || r === "SUPPLEMENTARY") return "To sit for SUPP Exam when next offered";
        if (r === "DISCO" || r === "DISCONTINUED" || r === "FAIL") return "To Retake All Modules Next Semester";
        if (r === "INCOMPLETE") return "To sit for Incomplete Module";
        return null;
      };
      const action = getAction(overallRemark);

      return `
        <div style="margin-bottom:24px">
          <h3 style="font-size:13px;font-weight:700;margin:0 0 4px 0">End of Semester ${escapeHtml(group.semester)} — Academic Year ${escapeHtml(group.academicYear)}</h3>
          <p style="font-size:11px;color:#6b7280;margin:0 0 8px 0">Exam Session: ${escapeHtml(group.examSession)}</p>
          <table style="width:100%;border-collapse:collapse;font-size:12px">
            <thead>
              <tr style="background:#f9fafb">
                <th style="border:1px solid #e5e7eb;padding:6px 8px;text-align:left;width:40px">S/N</th>
                <th style="border:1px solid #e5e7eb;padding:6px 8px;text-align:left">Module Code</th>
                <th style="border:1px solid #e5e7eb;padding:6px 8px;text-align:left">Module Name</th>
                <th style="border:1px solid #e5e7eb;padding:6px 8px;text-align:center">Grade</th>
                <th style="border:1px solid #e5e7eb;padding:6px 8px;text-align:center">Remark</th>
              </tr>
            </thead>
            <tbody>
              ${group.results.map((r, idx) => `
                <tr>
                  <td style="border:1px solid #e5e7eb;padding:5px 8px">${idx + 1}</td>
                  <td style="border:1px solid #e5e7eb;padding:5px 8px;font-family:monospace">${escapeHtml(r.moduleCode || "—")}</td>
                  <td style="border:1px solid #e5e7eb;padding:5px 8px">${escapeHtml(r.moduleName)}</td>
                  <td style="border:1px solid #e5e7eb;padding:5px 8px;text-align:center"><strong>${escapeHtml(r.grade || "—")}</strong></td>
                  <td style="border:1px solid #e5e7eb;padding:5px 8px;text-align:center">${remarkBadge(r.remark)}</td>
                </tr>
              `).join("")}
            </tbody>
          </table>
          <div style="margin-top:8px;padding-top:8px;border-top:1px solid #e5e7eb;font-size:11px">
            ${modulesToRetake.length > 0 ? `<p style="margin:0 0 4px 0"><strong>Modules to Retake:</strong> <span style="font-family:monospace">${escapeHtml(modulesToRetake.map(m => m.moduleCode).join(", "))}</span></p>` : ""}
            <p style="margin:0;display:flex;gap:24px;flex-wrap:wrap">
              <span><strong>Overall Remark:</strong> ${remarkBadge(overallRemark)}</span>
              <span><strong>GPA:</strong> ${escapeHtml(gpa || "—")}</span>
              ${action ? `<span><strong>Action:</strong> ${escapeHtml(action)}</span>` : ""}
            </p>
          </div>
        </div>
      `;
    }).join("");

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const logoUrl = window.location.origin + "/images/college-logo.png";

    printWindow.document.write(`<!DOCTYPE html>
<html><head><title>Results Slip - ${escapeHtml(student.first_name)} ${escapeHtml(student.last_name)}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin: 0; padding: 0; color: #111827; position: relative; min-height: 100vh; }
  .page-content { padding: 24px 32px; position: relative; z-index: 1; }
  @media print { .page-content { padding: 12px 20px; } @page { margin: 10mm; } .decorative { display: block !important; } }
  .decorative-tr { position: fixed; top: -30px; right: -30px; width: 160px; height: 160px; border-radius: 50%; background: rgba(42,181,178,0.12); z-index: 0; }
  .decorative-tr2 { position: fixed; top: 40px; right: 60px; width: 80px; height: 80px; border-radius: 50%; background: rgba(42,181,178,0.08); z-index: 0; }
  .decorative-br { position: fixed; bottom: -40px; right: -40px; width: 200px; height: 200px; border-radius: 50%; background: rgba(42,181,178,0.10); z-index: 0; }
  .header-bar { background: linear-gradient(135deg, #2ab5b2, #1a8a87); padding: 16px 24px; text-align: center; color: white; margin-bottom: 20px; border-radius: 0 0 12px 12px; }
  .header-bar img { height: 56px; margin-bottom: 6px; }
  .header-bar h1 { font-size: 20px; margin: 0; font-weight: 700; letter-spacing: 1px; }
  .header-bar p { font-size: 12px; margin: 4px 0 0 0; opacity: 0.9; }
</style></head><body>
  <div class="decorative-tr"></div>
  <div class="decorative-tr2"></div>
  <div class="decorative-br"></div>
  <div class="page-content">
    <div class="header-bar">
      <img src="${escapeHtml(logoUrl)}" alt="College Logo" onerror="this.style.display='none'" />
      <h1>ST. DAVID COLLEGE OF HEALTH SCIENCES</h1>
      <p>Academic Results Slip</p>
    </div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:4px 24px;font-size:12px;margin-bottom:20px;padding:12px;background:#f9fafb;border-radius:8px;border:1px solid #e5e7eb">
      <p style="margin:2px 0"><strong>Name:</strong> ${escapeHtml(student.first_name)} ${escapeHtml(student.last_name)}</p>
      <p style="margin:2px 0"><strong>Student No:</strong> ${escapeHtml(student.student_number)}</p>
      <p style="margin:2px 0"><strong>Class:</strong> ${escapeHtml(student.class || "—")}</p>
      <p style="margin:2px 0"><strong>Program:</strong> ${escapeHtml(student.program || "—")}</p>
      ${student.nta_level ? `<p style="margin:2px 0"><strong>NTA Level:</strong> ${escapeHtml(student.nta_level)}</p>` : ""}
    </div>
    ${sessionsHtml}
    <div style="margin-top:32px;text-align:center;font-size:10px;color:#9ca3af;border-top:1px solid #e5e7eb;padding-top:12px">
      <p style="margin:0">Generated on ${new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}</p>
      <p style="margin:4px 0 0 0;color:#2ab5b2;font-weight:600">St. David College of Health Sciences</p>
    </div>
  </div>
</body></html>`);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 800);
  }, [student, grouped]);

  if (!student) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <AlertCircle className="h-8 w-8 mx-auto mb-2 text-muted-foreground/60" />
          <p className="text-sm">No student record found for your account.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <AnimatedAnnouncementFeed
        announcements={announcements}
        title="Examination Announcements & Notices"
        emptyMessage="No examination announcements at this time."
      />

      <ResultAccessGate
        studentId={student.id}
        semester={null}
        studentClass={student.class}
        examType={null}
      >
        {/* Student Info Header */}
        <Card className="border-primary/20">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-primary/10 p-2">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1 flex-1 text-sm">
                <div>
                  <span className="text-muted-foreground text-xs">Name</span>
                  <p className="font-medium">{student.first_name} {student.last_name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs">Student No.</span>
                  <p className="font-medium">{student.student_number}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs">Class</span>
                  <p className="font-medium">{student.class || "—"}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs">Program</span>
                  <p className="font-medium">{student.program || "—"}</p>
                </div>
                {student.nta_level && (
                  <div>
                    <span className="text-muted-foreground text-xs">NTA Level</span>
                    <p className="font-medium">{student.nta_level}</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Print/Download Button */}
        {grouped.length > 0 && (
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={handlePrint} className="gap-2">
              <Printer className="h-4 w-4" />
              Print / Save as PDF
            </Button>
          </div>
        )}

        {/* Results by Session */}
        {grouped.length === 0 ? (
          <Card>
            <CardContent className="py-6 text-center text-muted-foreground text-sm">
              <Info className="h-6 w-6 mx-auto mb-2 text-muted-foreground/50" />
              No published results available yet.
            </CardContent>
          </Card>
        ) : (
          grouped.map((group) => {
            // Use the first result's remark/GPA (same for all rows of same student in same session)
            const storedOverall = group.results.find((r) => r.overall_remark)?.overall_remark;
            let overallRemark = storedOverall || null;
            if (!overallRemark && group.results.length > 0) {
              const remarks = group.results.map(r => r.remark?.toUpperCase()).filter(Boolean);
              if (remarks.some(r => r === "DISCO" || r === "DISCONTINUED")) overallRemark = "DISCONTINUED";
              else if (remarks.some(r => r === "FAIL")) overallRemark = "FAIL";
              else if (remarks.some(r => r === "SUPP" || r === "SUPPLEMENT" || r === "SUPPLEMENTARY")) overallRemark = "SUPP";
              else overallRemark = "PASS";
            }
            const gpa = group.results.find((r) => r.gpa)?.gpa;

            // Modules to retake: any module without grade A-C or with SUPP/FAIL/DISCO remark
            const modulesToRetake = group.results.filter((r) => {
              const rm = r.remark?.toUpperCase();
              const g = r.grade?.toUpperCase();
              return rm === "SUPP" || rm === "SUPPLEMENTARY" || rm === "FAIL" || rm === "DISCONTINUED" || rm === "DISCO" || rm === "INCOMPLETE" || g === "F" || g === "ABS";
            });

            // Action derived from overall remark
            const getAction = (remark: string | null) => {
              if (!remark) return null;
              const r = remark.toUpperCase();
              if (r === "PASS") return "Proceed to Next Semester";
              if (r === "SUPP" || r === "SUPPLEMENTARY") return "To sit for SUPP Exam when next offered";
              if (r === "DISCO" || r === "DISCONTINUED") return "To Retake All Modules Next Semester";
              if (r === "INCOMPLETE") return "To sit for Incomplete Module";
              if (r === "FAIL") return "To Retake All Modules Next Semester";
              return null;
            };
            const action = getAction(overallRemark);

            return (
              <Card key={group.key}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <GraduationCap className="h-4 w-4 text-primary" />
                    End of Semester {group.semester} — Academic Year {group.academicYear}
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">
                    Exam Session: {group.examSession}
                  </p>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs w-10">S/N</TableHead>
                          <TableHead className="text-xs">Module Code</TableHead>
                          <TableHead className="text-xs">Module Name</TableHead>
                          <TableHead className="text-xs text-center">Grade</TableHead>
                          <TableHead className="text-xs text-center">Remark</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {group.results.map((r, idx) => (
                          <TableRow key={r.id}>
                            <TableCell className="text-xs text-muted-foreground">{idx + 1}</TableCell>
                            <TableCell className="text-xs font-mono">{r.moduleCode || "—"}</TableCell>
                            <TableCell className="text-xs">{r.moduleName}</TableCell>
                            <TableCell className="text-xs text-center">
                              <Badge variant="secondary" className="text-[10px]">{r.grade || "—"}</Badge>
                            </TableCell>
                            <TableCell className="text-xs text-center">
                              {r.remark ? (
                                <Badge variant={remarkColor(r.remark)} className={`text-[10px] ${remarkClass(r.remark)}`}>
                                  {r.remark}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Footer: Modules to Retake, Overall Remark, GPA, Action */}
                  <div className="mt-3 pt-3 border-t space-y-2 text-xs">
                    {modulesToRetake.length > 0 && (
                      <div className="flex gap-2">
                        <span className="text-muted-foreground font-medium min-w-[120px]">Modules to Retake:</span>
                        <span className="font-mono">
                          {modulesToRetake.map((m) => m.moduleCode).join(", ")}
                        </span>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-x-6 gap-y-2">
                      <div className="flex gap-2 items-center">
                        <span className="text-muted-foreground font-medium">Overall Remark:</span>
                        {overallRemark ? (
                          <Badge variant={remarkColor(overallRemark)} className={`text-[10px] ${remarkClass(overallRemark)}`}>
                            {overallRemark}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </div>
                      <div className="flex gap-2 items-center">
                        <span className="text-muted-foreground font-medium">GPA:</span>
                        <span className="font-medium text-foreground">{gpa || "—"}</span>
                      </div>
                      {action && (
                        <div className="flex gap-2 items-center">
                          <span className="text-muted-foreground font-medium">Action:</span>
                          <span className="font-medium text-foreground">{action}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </ResultAccessGate>
    </div>
  );
}
